import { makeStyles } from '@rbx/ui';
import type { TTheme } from '@rbx/ui';
import { WhatsNewTileSize } from '../../constants/tileConstants';

const MODAL_TOP_OFFSET_DESKTOP_PX = 133.58;
// Content (3-line title ~59px + gap 4px + metadata ~20px) + padding top 24 + padding bottom 24 = 131
export const CAROUSEL_TILE_HEIGHT = 131;
const MODAL_TOP_OFFSET_MOBILE_PX = 73.58;

/** Shared base styles for carousel tiles (loading skeleton + announcement card). */
export const getCarouselTileBaseStyles = (theme: TTheme) => ({
  display: 'flex',
  width: WhatsNewTileSize.small.width,
  minWidth: WhatsNewTileSize.small.width,
  height: CAROUSEL_TILE_HEIGHT,
  minHeight: CAROUSEL_TILE_HEIGHT,
  maxHeight: CAROUSEL_TILE_HEIGHT,
  padding: 24,
  alignItems: 'flex-start',
  gap: 'var(--Gap-Large, 16px)',
  borderRadius: 8,
  border: `1px solid ${theme.palette.surface.outline}`,
  background: theme.palette.surface[0],
  boxSizing: 'border-box' as const,
});

const useUpdatesStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    flex: '1 0 0',
    alignSelf: 'stretch',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    marginBottom: 12,
    [theme.breakpoints.down('Large')]: {
      padding: 'var(--Padding-XXSmall, 2px) 0',
      alignContent: 'center',
      gap: 'var(--Padding-XSmall, 4px) var(--Gap-Small, 8px)',
      flexWrap: 'wrap',
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  viewAllLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  // Mobile carousel only - height fits content + equal top/bottom padding
  carouselTileWrapper: {
    width: WhatsNewTileSize.small.width,
    minWidth: WhatsNewTileSize.small.width,
    height: CAROUSEL_TILE_HEIGHT,
    minHeight: CAROUSEL_TILE_HEIGHT,
    maxHeight: CAROUSEL_TILE_HEIGHT,
    flexShrink: 0,
  },
  // Mobile carousel only
  carouselWrapper: {
    position: 'relative',
    width: '100%',
  },
  title: {
    color: theme.palette.content.standard,
    fontSize: 20,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '140%',
  },
  noUpdatesText: {
    alignSelf: 'stretch',
    color: theme.palette.content.standard,
    textAlign: 'center',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '100%',
  },
  noUpdatesContainer: {
    display: 'flex',
    height: 137,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  noUpdatesTray: {
    display: 'flex',
    width: 306,
    padding: '16px 4px 12px 4px',
    alignItems: 'center',
    gap: 8,
    position: 'absolute',
    left: 12,
    justifyContent: 'center',
  },
  noUpdatesStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 12,
    width: 274,
  },
  tagButtonWrapper: {
    display: 'inline-flex',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: '"Builder Sans"',
    whiteSpace: 'nowrap',
  },
  moreWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minHeight: 24,
    maxHeight: 24,
  },
  moreMenu: {
    display: 'flex',
    width: 'max-content',
    minWidth: 118,
    padding: 4,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    position: 'absolute',
    right: 0,
    top: 28,
    borderRadius: 8,
    border: `1px solid ${theme.palette.surface.outline}`,
    background: theme.palette.surface[200],
    boxShadow:
      '0 2px 4px -0.5px rgba(4, 4, 8, 0.25), 0 10px 20px -0.75px rgba(4, 4, 8, 0.25), 0 16px 32px -1px rgba(4, 4, 8, 0.25), 0 48px 56px -1.5px rgba(4, 4, 8, 0.25)',
    zIndex: 1,
  },
  moreMenuItem: {
    display: 'flex',
    padding: '4px 12px',
    alignItems: 'center',
    gap: 12,
    alignSelf: 'stretch',
    whiteSpace: 'nowrap',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: theme.palette.content.standard,
    fontSize: '12px',
    lineHeight: '140%',
    '&:hover': {
      backgroundColor: theme.palette.states.hover,
    },
  },
  moreMenuItemActive: {
    backgroundColor: theme.palette.states.hover,
    color: theme.palette.content.standard,
  },
  moreMenuIcon: {
    color: theme.palette.content.standard,
  },
  tilesList: {
    display: 'flex',
    padding: 12,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 24,
    alignSelf: 'stretch',
    width: 330,
    maxWidth: 330,
    boxSizing: 'border-box',
    borderRadius: 12,
    border: `1px solid ${theme.palette.surface.outline}`,
    position: 'relative',
    minHeight: 0,
    [theme.breakpoints.down('Large')]: {
      width: '100%',
      maxWidth: '100%',
      maxHeight: 'none',
      border: 'none',
      padding: 0,
    },
  },
  tilesScrollArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 24,
    alignSelf: 'stretch',
    overflowY: 'auto',
    overflowX: 'hidden',
    height: 555,
    flexShrink: 0,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& > *': {
      flexShrink: 0,
      alignSelf: 'stretch',
      minWidth: 0,
    },
    [theme.breakpoints.down('Large')]: {
      height: 470,
    },
  },
  tilesScrollAreaHorizontal: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 8,
    alignSelf: 'stretch',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& > *': {
      flexShrink: 0,
      minWidth: 'calc(100% - 56px)',
      maxWidth: 'calc(100% - 56px)',
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    minHeight: 0,
    [theme.breakpoints.down('Medium')]: {
      width: '100%',
      maxHeight: `calc(100vh - ${MODAL_TOP_OFFSET_MOBILE_PX}px)`,
      overflow: 'hidden',
      borderRadius: 16,
      background: theme.palette.surface[0],
    },
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  modalDialogContent: {
    marginTop: MODAL_TOP_OFFSET_DESKTOP_PX,
    alignSelf: 'flex-start',
    [theme.breakpoints.down('Medium')]: {
      marginTop: MODAL_TOP_OFFSET_MOBILE_PX,
      marginLeft: 14,
      marginRight: 14,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      minHeight: 'auto',
      maxHeight: `calc(100vh - ${MODAL_TOP_OFFSET_MOBILE_PX}px)`,
    },
  },
  modalDialogOverlay: {
    zIndex: '10000 !important',
    alignItems: 'flex-start !important',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
  },
  modalHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    padding: '12px 20px 0',
    gap: 12,
    [theme.breakpoints.down('Medium')]: {
      padding: '12px 14px 0',
    },
  },
  modalTitle: {
    alignSelf: 'stretch',
    minWidth: 0,
    color: theme.palette.content.standard,
    fontSize: 20,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '140%',
    paddingRight: 36,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('Medium')]: {
      whiteSpace: 'normal',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
    },
  },
  modalCloseButton: {
    display: 'flex',
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    width: 24,
    height: 24,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    position: 'absolute',
    top: 12,
    right: 20,
  },
  modalMetaSection: {
    display: 'flex',
    padding: '4px 20px 16px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    alignSelf: 'stretch',
  },
  modalTakeaways: {
    display: 'flex',
    padding: '12px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    alignSelf: 'stretch',
    borderRadius: 8,
    background:
      theme.palette.mode === 'dark' ? 'rgba(208, 217, 251, 0.04)' : 'rgba(32, 34, 39, 0.04)',
  },
  modalTakeawaysContent: {
    alignSelf: 'stretch',
    color: theme.palette.content.standard,
    fontFamily: '"Builder Sans"',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '140%',
    whiteSpace: 'pre-line',
  },
  modalTakeawaysContentRich: {
    whiteSpace: 'normal',
    '& p': {
      margin: '8px 0 0',
    },
    '& ul, & ol': {
      margin: '8px 0 0',
      paddingLeft: 16,
    },
    '& pre': {
      margin: '8px 0 0',
      padding: '8px 12px',
      borderRadius: 6,
      background: theme.palette.surface[200],
      overflowX: 'auto',
    },
    '& code': {
      fontFamily: 'monospace',
    },
  },
  modalMetaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  modalMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  modalIcon: {
    width: 16,
    height: 16,
    aspectRatio: '1/1',
    color: theme.palette.content.muted,
  },
  modalMetaText: {
    color: theme.palette.content.muted,
    textAlign: 'center',
    fontFamily: '"Builder Sans"',
    fontSize: 12,
    fontWeight: 400,
  },
  modalImageSection: {
    display: 'flex',
    alignSelf: 'stretch',
    borderRadius: 'var(--Radius-Medium, 8px)',
    marginTop: 0,
    overflow: 'hidden',
    background: 'lightgray 50% / cover no-repeat',
  },
  modalImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: 'var(--Radius-Medium, 8px)',
    objectFit: 'cover',
    objectPosition: '50%',
  },
  modalActionsSection: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    padding: '8px 0',
  },
  modalViewDetailsButton: {
    display: 'flex',
    minWidth: 48,
    minHeight: 32,
    maxHeight: 32,
    padding: '0 12px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    border: '1px solid transparent',
    background: theme.palette.actionV2?.primaryBrand?.fill ?? theme.palette.actionV2?.primary?.fill,
    color: theme.palette.common?.white ?? '#fff',
    cursor: 'pointer',
    fontFamily: '"Builder Sans"',
    '& *': {
      cursor: 'inherit',
      color: 'inherit',
      fontFamily: 'inherit',
    },
  },
  modalFooterNavLinks: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flex: '1 0 0',
    gap: 8,
  },
  modalNavButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 8px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: theme.palette.content.standard,
    '& *': {
      cursor: 'inherit',
    },
  },
  modalNavButtonText: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    fontFamily: 'var(--Config-Text-Font, "Builder Sans")',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '100%',
    color: 'inherit',
  },
  modalNavIcon: {
    flexShrink: 0,
  },
  modalFooterNavDisabled: {
    color: theme.palette.content.muted,
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  collapseButton: {
    display: 'flex',
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: theme.palette.content.muted,
    borderRadius: 4,
    '&:hover': {
      color: theme.palette.content.standard,
      backgroundColor: theme.palette.states.hover,
    },
  },
  collapsedTooltipWrapper: {
    '& [data-radix-popper-content-wrapper]': {
      zIndex: '1100 !important',
    },
    '& [role="tooltip"]': {
      display: 'inline-flex',
      height: 'auto',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 8,
    },
  },
  collapsedTab: {
    display: 'flex',
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    border: `1px solid ${theme.palette.surface.outline}`,
    background:
      theme.palette.mode === 'dark' ? 'rgba(208, 217, 251, 0.04)' : 'rgba(32, 34, 39, 0.04)',
    cursor: 'pointer',
    color: theme.palette.content.muted,
    boxSizing: 'border-box',
    '&:hover': {
      color: theme.palette.content.standard,
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(208, 217, 251, 0.08)' : 'rgba(32, 34, 39, 0.08)',
    },
  },
}));

export default useUpdatesStyles;
